import { c as _c } from "react/compiler-runtime";
import { formatDistanceToNow } from 'date-fns';
import styled, { css } from 'styled-components';
import Icon from '../common/base/Icon';
import Text from '../common/base/Text';
import Tooltip from '../common/base/Tooltip';
import { Div } from '../common/helpers/StyledUtils';
import { FC, ReactNode, useContext } from 'react';
import Link from '../common/base/Link';
import { DashboardContext } from '../scenes/DashboardScene/DashboardContext';
import { capitalizeFirstLetter } from '../../utils';
import CopyableText from '../common/CopyableText';
import { formatDate, getTimezoneAbbreviation } from '../../utils/date';
import { CONTAINER_WIDTHS } from '@hookdeck/theme';
import { useDatePreferences } from '../hooks/useDatePreferences';
export const DASHBOARD_NAV_WIDTH = 224;
export const DASHBOARD_NAV_COLLAPSED_WIDTH = 44;
export const DashboardLayout = styled.div<{
  nav_collapsed: boolean;
  banner_active: boolean;
}>`
  background-color: ${({
  theme
}) => theme.colors.surface.base.background};
  min-height: ${({
  banner_active
}) => banner_active ? `calc(100vh - 32px)` : '100vh'};
  display: grid;
  grid-template-columns: ${({
  theme,
  nav_collapsed
}) => nav_collapsed ? `${theme.pxToRem(DASHBOARD_NAV_COLLAPSED_WIDTH)} 1fr` : `${theme.pxToRem(DASHBOARD_NAV_WIDTH)} 1fr`};
`;
export const DashboardLayoutContent = styled.div<{
  banner_active: boolean;
}>`
  background-color: ${({
  theme
}) => theme.colors.surface.base.background};
  min-height: ${({
  banner_active
}) => banner_active ? `calc(100vh - 32px)` : '100vh'};
  flex-grow: 1;
  min-width: 0;
  position: relative;
  top: 0px;
`;
export const StyledViewLayout = styled.div`
  display: flex;
  height: 100%;
  background-color: ${({
  theme
}) => theme.colors.surface.base.surface};
`;
export const StyledViewNav = styled(Div)<{
  relative?: boolean;
}>(({
  theme,
  relative = false
}) => {
  const {
    is_degraded_banner_active
  } = useContext(DashboardContext);
  return css`
    position: ${relative ? 'relative' : 'sticky'};
    top: ${is_degraded_banner_active ? '32px' : 0};
    flex-shrink: 0;
    background-color: ${theme.colors.surface.base.surface};
    z-index: ${theme.zindex.sticky};
  `;
});
export const StyledViewNavHeader = styled(Div)(({
  theme
}) => css`
    padding: ${theme.spacing(0)} ${theme.spacing(6)} ${theme.spacing(0)} ${theme.spacing(6)};
    border-bottom: ${theme.border};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.spacing(4)};
    flex-shrink: 0;
    height: 53px;
    box-sizing: border-box;
    max-width: 100%;
    overflow-x: auto;
    &::-webkit-scrollbar {
      display: none;
    }
  `);
export const StyledViewNavSecondary = styled(Div)(({
  theme
}) => css`
    padding: ${theme.spacing(3)} ${theme.spacing(6)} ${theme.spacing(0)} ${theme.spacing(6)};
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
    box-sizing: border-box;
    border-bottom: ${theme.border};
  `);
export const ViewBreadcrumbsTitle: FC<{
  breadcrumbs: {
    label: string;
    to?: string;
    highlight?: boolean;
    ellipsis?: boolean;
    max_width_vw?: number;
  }[];
}> = t0 => {
  const $ = _c(6);
  const {
    breadcrumbs
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      align: "center",
      gap: 2
    };
    t2 = {
      overflow: "hidden",
      minWidth: 0
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== breadcrumbs) {
    t3 = breadcrumbs.map((t4, index) => {
      const {
        label,
        to,
        highlight,
        ellipsis,
        max_width_vw
      } = t4;
      const has_next = !!breadcrumbs[index + 1];
      const element = to ? <Link neutral={true} to={to}><Text semi={true} ellipsis={!has_next || ellipsis} title={label} style={{
          minWidth: 0
        }}>{label}</Text></Link> : <Text semi={true} neutral={!highlight} ellipsis={!has_next || ellipsis} title={label} style={{
        minWidth: 0
      }}>{label}</Text>;
      return [<Div key={label} max_w={{
        vw: max_width_vw
      }} style={{
        minWidth: 0,
        overflow: "hidden"
      }}>{element}</Div>, has_next && <Text key={`${label}-separator`} semi={true} neutral={!highlight}>/</Text>];
    });
    $[2] = breadcrumbs;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <Div flex={t1} style={t2}>{t3}</Div>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
};
const StyledViewTitle = styled.div(({
  theme
}) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(2)};
  `);
export const ViewTitle = t0 => {
  const $ = _c(10);
  const {
    title,
    tooltip,
    link,
    link_label
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <Text semi={true} size="m">{title}</Text>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== link || $[3] !== link_label || $[4] !== title || $[5] !== tooltip) {
    t2 = tooltip && link && <Tooltip tooltip={tooltip} cta={{
      icon: "docs",
      label: link_label || `${capitalizeFirstLetter(title.toLowerCase())} docs`,
      to: link
    }}><Icon icon="info" muted={true} /></Tooltip>;
    $[2] = link;
    $[3] = link_label;
    $[4] = title;
    $[5] = tooltip;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = <StyledViewTitle>{t1}{t2}</StyledViewTitle>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};
export const ViewSidebarTitle = t0 => {
  const $ = _c(2);
  const {
    title
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <StyledViewTitle><Text semi={true} size="m">{title}</Text></StyledViewTitle>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export const StyledViewNavActions = styled.div(({
  theme
}) => css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${theme.spacing(3)};
  `);
export const StyledViewSidebarActions = styled.div(({
  theme
}) => css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${theme.spacing(2)};
  `);
export const StyledViewContent = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  margin: 0 auto;
  min-width: 0;
  min-height: 100%;
`;

// Header row only (breadcrumbs); add VIEW_NAV_SECONDARY_HEIGHT when the tabs row is present.
export const VIEW_NAV_HEADER_HEIGHT = 53;
export const VIEW_NAV_HEIGHT = 106;
export const StyledViewSplitPaneLayout = styled.div<{
  banner_active?: boolean;
  nav_height?: number;
}>(({
  theme,
  banner_active,
  nav_height = VIEW_NAV_HEIGHT
}) => {
  const nav_offset = banner_active ? nav_height + 32 : nav_height;
  return css`
    display: flex;
    flex: 1 1 auto;
    align-items: flex-start;
    min-height: calc(100vh - ${nav_offset}px);
    background-color: ${theme.colors.surface.base.surface};
    --view-split-pane-sticky-top: ${theme.pxToRem(nav_offset)};
    --view-split-pane-height: calc(100vh - ${theme.pxToRem(nav_offset)});
  `;
});
export const StyledViewSplitPaneSidebar = styled.aside(({
  theme
}) => css`
    position: sticky;
    top: var(--view-split-pane-sticky-top);
    width: ${theme.pxToRem(CONTAINER_WIDTHS.small)};
    min-width: ${theme.pxToRem(CONTAINER_WIDTHS.small)};
    max-width: ${theme.pxToRem(CONTAINER_WIDTHS.small)};
    height: var(--view-split-pane-height);
    max-height: var(--view-split-pane-height);
    /* Top padding is 4px shorter so large title text optically aligns with the content pane. */
    padding: ${theme.spacing(6)};
    box-sizing: border-box;
    border-right: ${theme.border};
    overflow-y: auto;
  `);
export const StyledViewSplitPaneFooter = styled.div(({
  theme
}) => css`
    padding: ${theme.spacing(3)} ${theme.spacing(6)};
    border-top: ${theme.border};
    background-color: ${theme.colors.surface.base.surface};
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: ${theme.zindex.sticky};
  `);
export const StyledViewSplitPaneContent = styled.main(({
  theme
}) => css`
    flex: 1 1 auto;
    min-width: 0;
    /* Top padding matches the sidebar's shortened top padding so titles line up. */
    padding: ${theme.spacing(6)};
    box-sizing: border-box;
  `);
export const ViewMetadataItem = t0 => {
  const $ = _c(10);
  const {
    label,
    children
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      columns: 1,
      gap_y: 1
    };
    t2 = {
      minWidth: 0
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== label) {
    t3 = <Text muted={true} ellipsis={true}>{label}</Text>;
    $[2] = label;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      minWidth: 0
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== children) {
    t5 = <Div style={t4}>{children}</Div>;
    $[5] = children;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t3 || $[8] !== t5) {
    t6 = <Div grid={t1} style={t2}>{t3}{t5}</Div>;
    $[7] = t3;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
};
export const ViewMetadataDescription = t0 => {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      overflowWrap: "anywhere",
      whiteSpace: "normal",
      minWidth: 0
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <Text style={t1}>{children}</Text>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};

// Vertical stack for the split pane sidebar: header + metadata items with even spacing.
export const StyledViewSplitPaneSidebarBody = styled.div(({
  theme
}) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(4)};
  `);
const StyledViewSplitPaneSidebarSection = styled.div<{
  $bordered?: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: ${({
  theme
}) => theme.spacing(4)};
  ${({
  $bordered,
  theme
}) => $bordered ? `
          margin-top: ${theme.spacing(2)};
          border-top: ${theme.border};
          padding-top: ${theme.spacing(6)};
        ` : ''}
`;
export const ViewSplitPaneSidebarSection = t0 => {
  const $ = _c(6);
  const {
    title,
    bordered,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = title && <Text bold={true} size="m">{title}</Text>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== bordered || $[3] !== children || $[4] !== t1) {
    t2 = <StyledViewSplitPaneSidebarSection $bordered={bordered}>{t1}{children}</StyledViewSplitPaneSidebarSection>;
    $[2] = bordered;
    $[3] = children;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
export const ViewSplitPaneSidebarHeader = t0 => {
  const $ = _c(15);
  const {
    title,
    action,
    badges
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      direction: "column",
      gap: 2
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = action ? -0.5 : 0;
  let t3;
  if ($[1] !== t2) {
    t3 = {
      t: t2
    };
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      align: "center",
      justify: "space-between",
      gap: 4
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== title) {
    t5 = <Text bold={true} size="l">{title}</Text>;
    $[4] = title;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== action || $[7] !== t5) {
    t6 = <Div flex={t4}>{t5}{action}</Div>;
    $[6] = action;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== badges) {
    t7 = badges && <Div flex={{
      align: "center",
      gap: 2,
      wrap: true
    }}>{badges}</Div>;
    $[9] = badges;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t3 || $[12] !== t6 || $[13] !== t7) {
    t8 = <Div flex={t1} m={t3}>{t6}{t7}</Div>;
    $[11] = t3;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  return t8;
};
export const ViewMetadataCopyable = t0 => {
  const $ = _c(3);
  const {
    value,
    wrap
  } = t0;
  let t1;
  if ($[0] !== value || $[1] !== wrap) {
    t1 = <CopyableText value={value} wrap={wrap} mono={true} />;
    $[0] = value;
    $[1] = wrap;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const formatMetadataTimestamp = (date: string, timezone: string) => `${formatDate(date, 'yyyy-MM-dd HH:mm:ss', timezone)} ${getTimezoneAbbreviation(timezone, date)}`;
export const ViewMetadataDate = t0 => {
  const $ = _c(10);
  const {
    date
  } = t0;
  const {
    timezone
  } = useDatePreferences();
  let t1;
  if ($[0] !== date || $[1] !== timezone) {
    t1 = formatMetadataTimestamp(date, timezone);
    $[0] = date;
    $[1] = timezone;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== date) {
    t2 = capitalizeFirstLetter(formatDistanceToNow(new Date(date), {
      addSuffix: true
    }));
    $[3] = date;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <Text>{t2}</Text>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t3) {
    t4 = <Tooltip tooltip={t1} side="bottom">{t3}</Tooltip>;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};

// Heading row at the top of the split pane content area (title + optional actions).
export const ViewSplitPaneContentHeader = t0 => {
  const $ = _c(15);
  const {
    title,
    elements,
    actions
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      justify: "space-between",
      align: "center",
      gap: 4
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = actions ? -0.5 : 0;
  let t3;
  if ($[1] !== t2) {
    t3 = {
      b: 4,
      t: t2
    };
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      align: "center",
      gap: 2
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== title) {
    t5 = <Text as="h2" size="l" bold={true} m={0}>{title}</Text>;
    $[4] = title;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== elements || $[7] !== t5) {
    t6 = <Div flex={t4}>{t5}{elements}</Div>;
    $[6] = elements;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== actions) {
    t7 = actions && <Div flex={{
      gap: 3,
      align: "center"
    }}>{actions}</Div>;
    $[9] = actions;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t3 || $[12] !== t6 || $[13] !== t7) {
    t8 = <Div flex={t1} m={t3}>{t6}{t7}</Div>;
    $[11] = t3;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  return t8;
};

// Stacked content sections inside the split pane content area. Replaces ad-hoc
// Container + Divider spacing: sections get even vertical rhythm and a separator.
export const StyledViewSplitPaneSection = styled.div<{
  max_width?: keyof typeof CONTAINER_WIDTHS;
}>(({
  theme,
  max_width
}) => css`
    width: 100%;
    box-sizing: border-box;
    ${max_width ? css`
          > * {
            max-width: ${theme.pxToRem(CONTAINER_WIDTHS[max_width])};
          }
        ` : ''}
    &:not(:first-of-type) {
      margin-top: ${theme.spacing(8)};
      padding-top: ${theme.spacing(8)};
      border-top: ${theme.border};
    }
  `);
export const StyledViewSidebar = styled.div<{
  width?: 'small' | 'large';
}>(({
  theme,
  width
}) => {
  const {
    is_degraded_banner_active
  } = useContext(DashboardContext);
  const computed_width = width === 'large' ? theme.pxToRem(904) : theme.pxToRem(480);
  return css`
    position: sticky;
    display: flex;
    flex-direction: column;
    top: ${is_degraded_banner_active ? '32px' : '0px'};
    right: 0;

    flex-basis: ${computed_width};
    width: ${computed_width};
    min-width: ${computed_width};
    background-color: ${theme.colors.surface.base.surface};
    border-left: ${theme.border};
    height: calc(100vh - ${is_degraded_banner_active ? '32px' : '0px'});
    overflow-y: auto;
    z-index: ${theme.zindex.sticky};
  `;
});
export const StyledViewSidebarSection = styled(Div)(({
  theme
}) => css`
    padding: ${theme.spacing(4)};
    &:not(:last-child) {
      border-bottom: ${theme.border};
    }
  `);
export const StyledViewSidebarNav = styled(StyledViewNav)`
  top: 0;
  ${StyledViewNavHeader} {
    padding: ${({
  theme
}) => theme.spacing(0)} ${({
  theme
}) => theme.spacing(6)}
      ${({
  theme
}) => theme.spacing(0)} ${({
  theme
}) => theme.spacing(6)};
  }
`;
export const StyledViewSidebarFooter = styled.div`
  ${({
  theme
}) => css`
    display: flex;
    padding: ${theme.spacing(3)} ${theme.spacing(6)};
    border-top: ${theme.border};
    background-color: ${theme.colors.surface.base.surface};
  `}
`;
export const StyledViewSidebarScrollableSection = styled.div`
  flex-grow: 1;
  overflow-y: scroll;

  &::-webkit-scrollbar {
    display: none;
  }
`;